import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Switch } from "@package/subframe/components/Switch";
import { TextField } from "@package/subframe/components/TextField";
import {
  FeatherAlertTriangle,
  FeatherEyeOff,
  FeatherGripVertical,
  FeatherPlus,
  FeatherTrash2,
} from "@subframe/core";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { UpgradePrompt } from "@/components/shared/UpgradePrompt";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  AddQuestionDtoQuestionType,
  type UpdateQuestionDto,
} from "@/generated/model";
import {
  useAddQuestionCommandControllerAddQuestion,
  useCreateTemplateVersionCommandControllerCreateVersion,
  useDeleteQuestionCommandControllerDeleteQuestion,
  useUpdateQuestionCommandControllerUpdateQuestion,
} from "@/generated/questionnaire-templates/questionnaire-templates";
import { useFeatureGate } from "@/hooks/use-feature-gate";
import {
  getQuestionTypeLabel,
  QUESTION_TYPE_LABELS,
} from "@/lib/checklist-utils";
import { FEATURES } from "@/lib/features";
import { generateClientId } from "@/lib/offline/db";

interface Question {
  id: string;
  questionText: string;
  questionType?: string;
  orderIndex?: number;
  isRequired?: boolean;
  isHidden?: boolean;
  options?: { choices?: string[] };
}

interface EditTabProps {
  templateId: string;
  questions: Question[];
  /**
   * Status of the template itself — not of a nursery's schedule config. A published
   * template must be versioned rather than edited in place, so its questions stay
   * pinned for checklists staff are part-way through (NAU-385).
   */
  isTemplatePublished?: boolean;
  onQuestionsChanged: () => void;
  onTemplateVersionCreated?: (newTemplateId: string) => void;
}

interface EditableItem {
  id: string;
  label: string;
  isRequired: boolean;
  isHidden: boolean;
  questionType: AddQuestionDtoQuestionType;
  orderIndex: number;
  isDirty: boolean;
  isNew: boolean;
}

function toEditableItem(q: Question): EditableItem {
  return {
    id: q.id,
    label: q.questionText,
    isRequired: q.isRequired ?? true,
    isHidden: q.isHidden ?? false,
    questionType:
      (q.questionType as AddQuestionDtoQuestionType) ||
      AddQuestionDtoQuestionType.YES_NO,
    orderIndex: q.orderIndex ?? 0,
    isDirty: false,
    isNew: false,
  };
}

function saveErrorMessage(err: unknown): string {
  const fallback = "Failed to save questions";
  if (!axios.isAxiosError(err)) {
    return fallback;
  }

  switch (err.response?.status) {
    case 400:
      return "A checklist must keep at least one question";
    case 403:
      return "Question customisation requires the Boost plan";
    case 404:
      return "That question no longer exists — the list has been refreshed";
    case 409:
      return "This question already has recorded answers, so it can't be removed";
    default:
      return fallback;
  }
}

function SortableQuestionItem({
  item,
  index,
  isSaving,
  canRemove,
  onToggleHidden,
  onToggleRequired,
  onUpdateLabel,
  onUpdateType,
  onRemove,
}: {
  item: EditableItem;
  index: number;
  isSaving: boolean;
  canRemove: boolean;
  onToggleHidden: (id: string) => void;
  onToggleRequired: (id: string) => void;
  onUpdateLabel: (id: string, label: string) => void;
  onUpdateType: (id: string, type: AddQuestionDtoQuestionType) => void;
  onRemove: (id: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: isSaving });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-start gap-3 rounded-md border border-solid px-4 py-3 ${
        item.isHidden
          ? "border-neutral-200 bg-neutral-100 opacity-60"
          : "border-secondary-beige-200 bg-neutral-50"
      }`}
    >
      <button
        type="button"
        className="mt-2 cursor-grab active:cursor-grabbing text-subtext-color hover:text-default-font"
        {...attributes}
        {...listeners}
      >
        <FeatherGripVertical className="h-5 w-5" />
      </button>

      <div className="flex grow flex-col gap-3">
        {/* Question text */}
        <div className="flex w-full items-center gap-3">
          <span className="text-body-bold font-body-bold text-subtext-color w-6">
            {index + 1}.
          </span>
          {item.isNew ? (
            <TextField
              className="h-auto grow shrink-0 basis-0"
              variant="outline"
            >
              <TextField.Input
                placeholder="Enter question text..."
                value={item.label}
                onChange={(e) => onUpdateLabel(item.id, e.target.value)}
                disabled={isSaving}
              />
            </TextField>
          ) : (
            <span
              className={`text-body-bold font-body-bold grow ${item.isHidden ? "text-subtext-color line-through" : "text-default-font"}`}
            >
              {item.label}
            </span>
          )}
        </div>

        {/* Type selector for new questions */}
        {item.isNew && (
          <div className="flex items-center gap-2 pl-9">
            <span className="text-body font-body text-subtext-color">
              Type:
            </span>
            {Object.entries(QUESTION_TYPE_LABELS).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`text-caption font-caption px-2 py-1 rounded border transition-colors ${
                  item.questionType === value
                    ? "border-neutral-600 text-neutral-900 bg-white"
                    : "border-secondary-beige-200 text-subtext-color bg-white hover:border-neutral-400"
                }`}
                onClick={() =>
                  onUpdateType(item.id, value as AddQuestionDtoQuestionType)
                }
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Badges and toggles row */}
        <div className="flex items-center gap-4 pl-9">
          {!item.isNew && (
            <span className="text-caption font-caption px-2 py-0.5 rounded border border-neutral-300 text-neutral-700 bg-white">
              {getQuestionTypeLabel(item.questionType)}
            </span>
          )}
          {item.isHidden && (
            <span className="flex items-center gap-1 text-caption font-caption text-neutral-500">
              <FeatherEyeOff className="h-3 w-3" />
              Hidden
            </span>
          )}
        </div>

        {/* Toggle controls */}
        <div className="flex items-center gap-6 pl-9">
          <div className="flex items-center gap-2">
            <Switch
              checked={!item.isHidden}
              onCheckedChange={() => onToggleHidden(item.id)}
              disabled={isSaving}
            />
            <span className="text-body font-body text-subtext-color whitespace-nowrap">
              {item.isHidden ? "Hidden" : "Visible"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={item.isRequired}
              onCheckedChange={() => onToggleRequired(item.id)}
              disabled={isSaving}
            />
            <span className="text-body font-body text-subtext-color">
              Required
            </span>
          </div>
        </div>
      </div>

      <IconButton
        variant="neutral-tertiary"
        size="medium"
        icon={<FeatherTrash2 />}
        aria-label={`Remove question ${index + 1}`}
        onClick={() => onRemove(item.id)}
        disabled={isSaving || !canRemove}
      />
    </div>
  );
}

export function EditTab({
  templateId,
  questions,
  isTemplatePublished,
  onQuestionsChanged,
  onTemplateVersionCreated,
}: EditTabProps) {
  const { hasFeature, requiredPlan } = useFeatureGate(
    FEATURES.QUESTION_CUSTOMISATION,
  );

  const [items, setItems] = useState<EditableItem[]>([]);
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showVersionConfirm, setShowVersionConfirm] = useState(false);

  const updateQuestionMutation =
    useUpdateQuestionCommandControllerUpdateQuestion();
  const addQuestionMutation = useAddQuestionCommandControllerAddQuestion();
  const deleteQuestionMutation =
    useDeleteQuestionCommandControllerDeleteQuestion();
  const createVersionMutation =
    useCreateTemplateVersionCommandControllerCreateVersion();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Sync from server questions when they change
  useEffect(() => {
    const sorted = [...questions].sort(
      (a, b) => (a.orderIndex || 0) - (b.orderIndex || 0),
    );
    setItems(sorted.map(toEditableItem));
    setDeletedIds([]);
  }, [questions]);

  const invalidateAndRefresh = useCallback(async () => {
    onQuestionsChanged();
  }, [onQuestionsChanged]);

  const toggleHidden = (id: string) => {
    setItems(
      items.map((item) =>
        item.id === id
          ? { ...item, isHidden: !item.isHidden, isDirty: true }
          : item,
      ),
    );
  };

  const toggleRequired = (id: string) => {
    setItems(
      items.map((item) =>
        item.id === id
          ? { ...item, isRequired: !item.isRequired, isDirty: true }
          : item,
      ),
    );
  };

  const updateLabel = (id: string, label: string) => {
    setItems(
      items.map((item) =>
        item.id === id ? { ...item, label, isDirty: true } : item,
      ),
    );
  };

  const updateType = (id: string, type: AddQuestionDtoQuestionType) => {
    setItems(
      items.map((item) =>
        item.id === id ? { ...item, questionType: type, isDirty: true } : item,
      ),
    );
  };

  const addQuestion = () => {
    setItems([
      ...items,
      {
        id: generateClientId(),
        label: "",
        isRequired: true,
        isHidden: false,
        questionType: AddQuestionDtoQuestionType.YES_NO,
        orderIndex: items.length,
        isDirty: true,
        isNew: true,
      },
    ]);
  };

  const removeQuestion = (id: string) => {
    const item = items.find((i) => i.id === id);
    if (!item) return;

    // Track deletion of existing (server-side) questions
    if (!item.isNew) {
      setDeletedIds([...deletedIds, id]);
    }

    setItems(items.filter((i) => i.id !== id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setItems((prev) => {
        const oldIndex = prev.findIndex((item) => item.id === active.id);
        const newIndex = prev.findIndex((item) => item.id === over.id);
        const reordered = arrayMove(prev, oldIndex, newIndex);
        // Mark all as dirty since order changed
        return reordered.map((item, i) => ({
          ...item,
          orderIndex: i,
          isDirty: true,
        }));
      });
    }
  };

  const handleSave = () => {
    const dirtyItems = items.filter((item) => item.isDirty);
    const hasChanges = dirtyItems.length > 0 || deletedIds.length > 0;
    if (!hasChanges) return;

    // Validate new questions have text
    const emptyNew = items.filter((item) => item.isNew && !item.label.trim());
    if (emptyNew.length > 0) {
      toast.error("Please enter text for all new questions");
      return;
    }

    if (isTemplatePublished) {
      setShowVersionConfirm(true);
      return;
    }

    void executeSave();
  };

  const executeSave = async () => {
    setShowVersionConfirm(false);
    const dirtyItems = items.filter((item) => item.isDirty);
    const newItems = items.filter((item) => item.isNew);
    const existingDirty = dirtyItems.filter((item) => !item.isNew);

    // Without this a delete-only save on a draft template sent no requests at all and
    // still reported success (NAU-385).
    const requestCount =
      newItems.length + existingDirty.length + deletedIds.length;
    if (!isTemplatePublished && requestCount === 0) {
      return;
    }

    setIsSaving(true);
    try {
      if (isTemplatePublished) {
        // Published template: create a single new version with all changes
        const result = await createVersionMutation.mutateAsync({
          templateId,
          data: {
            questions:
              existingDirty.length > 0
                ? existingDirty.map((item) => ({
                    questionId: item.id,
                    isHidden: item.isHidden,
                    isRequired: item.isRequired,
                    questionText: item.label,
                  }))
                : undefined,
            newQuestions:
              newItems.length > 0
                ? newItems.map((item, _i) => ({
                    questionText: item.label,
                    questionType:
                      item.questionType as unknown as import("@/generated/model").NewQuestionDtoQuestionType,
                    isRequired: item.isRequired,
                    orderIndex: items.indexOf(item),
                  }))
                : undefined,
            deletedQuestionIds: deletedIds.length > 0 ? deletedIds : undefined,
          },
        });

        toast.success("New version created");
        await invalidateAndRefresh();
        if (onTemplateVersionCreated && result?.data) {
          onTemplateVersionCreated((result.data as { id: string }).id);
        }
      } else {
        // Draft template: apply changes individually
        for (const item of newItems) {
          await addQuestionMutation.mutateAsync({
            templateId,
            data: {
              questionText: item.label,
              questionType:
                item.questionType as unknown as import("@/generated/model").AddQuestionDtoQuestionType,
              isRequired: item.isRequired,
              orderIndex: items.indexOf(item),
            },
          });
        }

        for (const item of existingDirty) {
          const dto: UpdateQuestionDto = {
            questionText: item.label,
            questionType:
              item.questionType as unknown as UpdateQuestionDto["questionType"],
            isRequired: item.isRequired,
            isHidden: item.isHidden,
            orderIndex: items.indexOf(item),
          };
          await updateQuestionMutation.mutateAsync({
            templateId,
            questionId: item.id,
            data: dto,
          });
        }

        // Deletions go last: the bin icon counts unsaved new rows too, so deleting
        // first can trip the server's last-question guard before the adds land.
        for (const questionId of deletedIds) {
          await deleteQuestionMutation.mutateAsync({ templateId, questionId });
        }

        toast.success("Questions saved");
        await invalidateAndRefresh();
      }
    } catch (err) {
      // Earlier writes in the loop may already have landed, so resync the list
      // before surfacing the error rather than leaving stale rows on screen.
      await invalidateAndRefresh();
      toast.error(saveErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges =
    items.some((item) => item.isDirty) || deletedIds.length > 0;

  if (!hasFeature) {
    return (
      <UpgradePrompt
        requiredPlan={requiredPlan}
        featureName="Question customisation"
      />
    );
  }

  return (
    <div className="flex w-full items-center justify-center px-6 pt-8 pb-6">
      <div className="flex w-256 max-w-full flex-none flex-col items-start rounded-lg bg-default-background mb-8">
        <div className="flex w-full flex-col gap-6 px-8 py-8">
          {/* Header */}
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-primary-navy-800">
                Edit questions
              </span>
              <span className="text-body font-body text-subtext-color">
                Add, remove, reorder, and toggle visibility of checklist
                questions.
              </span>
            </div>
            <Button
              variant="neutral-secondary"
              icon={<FeatherPlus />}
              onClick={addQuestion}
              disabled={isSaving}
            >
              Add question
            </Button>
          </div>

          {/* Questions list */}
          {items.length === 0 ? (
            <div className="flex w-full flex-col items-center gap-4 rounded-md border border-dashed border-neutral-300 px-8 py-12">
              <span className="text-body font-body text-subtext-color">
                No questions in this template.
              </span>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={items.map((i) => i.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex flex-col gap-3">
                  {items.map((item, index) => (
                    <SortableQuestionItem
                      key={item.id}
                      item={item}
                      index={index}
                      isSaving={isSaving}
                      canRemove={items.length > 1}
                      onToggleHidden={toggleHidden}
                      onToggleRequired={toggleRequired}
                      onUpdateLabel={updateLabel}
                      onUpdateType={updateType}
                      onRemove={removeQuestion}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {/* Save footer */}
          <div className="flex w-full items-center justify-end gap-3 border-t border-solid border-neutral-200 pt-6">
            <Button
              variant="brand-primary"
              size="large"
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
            >
              {isSaving ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </div>
      </div>

      {/* Version confirmation dialog */}
      <Dialog open={showVersionConfirm} onOpenChange={setShowVersionConfirm}>
        <DialogContent className="p-0 sm:max-w-[448px]">
          <div className="flex h-full w-full flex-col items-start gap-6 px-8 py-8">
            <div className="flex w-full items-center gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
                <FeatherAlertTriangle className="text-amber-600" />
              </div>
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Create new version?
                </span>
                <span className="text-body font-body text-subtext-color">
                  This template is published and in use.
                </span>
              </div>
            </div>

            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body font-body text-default-font">
                Saving will create a new version of this template. This will:
              </span>
              <div className="flex w-full flex-col items-start gap-2 pl-2">
                <span className="text-body font-body text-default-font">
                  • Regenerate all upcoming (pending) instances
                </span>
                <span className="text-body font-body text-default-font">
                  • Keep in-progress instances on the current version
                </span>
                <span className="text-body font-body text-default-font">
                  • Preserve all historical completion data
                </span>
              </div>
            </div>

            <div className="flex w-full items-center justify-end gap-2">
              <Button
                variant="neutral-secondary"
                size="large"
                onClick={() => setShowVersionConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="brand-primary"
                size="large"
                onClick={executeSave}
                disabled={isSaving}
              >
                {isSaving ? "Creating version..." : "Create new version"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
